import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Check, Shield, Eye, UsersFour, ArrowRight, WhatsappLogo, Envelope } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SecuritePage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", service_type: "securite", message: "", preferred_contact: "email",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        toast.success(t("securite.quote_desc"));
        setFormData({ name: "", email: "", phone: "", service_type: "securite", message: "", preferred_contact: "email" });
      }
    } catch {
      toast.error("Erreur");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const features = [
    { icon: Shield, title: t("securite.features_1_title"), description: t("securite.features_1_desc") },
    { icon: Eye, title: t("securite.features_2_title"), description: t("securite.features_2_desc") },
    { icon: UsersFour, title: t("securite.features_3_title"), description: t("securite.features_3_desc") },
  ];

  const services = [
    t("securite.service_1"), t("securite.service_2"), t("securite.service_3"), t("securite.service_4"),
    t("securite.service_5"), t("securite.service_6"), t("securite.service_7"), t("securite.service_8"),
  ];

  return (
    <main className="pt-20 bg-[#EDEBE4]" data-testid="securite-page">
      <SEO title="Sécurité Rapprochée" path="/services/securite" description="Service de sécurité rapprochée ALTURA à Paris. Agents formés, protection discrète, escorte événementielle. Confidentialité et professionnalisme garantis." />
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1543350155-2cf78fd1c43e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            alt="Bodyguard ALTURA"
            className="w-full h-full object-cover object-center"
          />
          <div className="hero-overlay-side absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 w-full">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-md hero-text-backdrop">
            <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-4">{t("securite.hero_subtitle")}</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-normal text-[#1A1A1A] leading-none">
              {t("securite.hero_title_1")}<br /><span className="italic">{t("securite.hero_title_2")}</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 sm:py-24 bg-[#E5E3DC]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white border border-[#1A1A1A]/10 p-8 text-center">
                <feature.icon size={40} weight="thin" className="text-[#1A1A1A] mx-auto mb-6" />
                <h3 className="font-heading text-xl text-[#1A1A1A] mb-4">{feature.title}</h3>
                <p className="text-[#1A1A1A]/70 font-light text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 sm:py-24 bg-[#EDEBE4]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="font-heading text-2xl lg:text-3xl text-[#1A1A1A] mb-8">{t("securite.services_title")}</h2>
              <p className="text-[#1A1A1A]/80 font-light leading-relaxed mb-8">{t("securite.services_desc")}</p>
              <ul className="space-y-4 mb-12">
                {services.map((service, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <Check size={20} weight="bold" className="text-[#1A1A1A] mt-0.5 flex-shrink-0" />
                    <span className="text-[#1A1A1A]/80 font-light">{service}</span>
                  </li>
                ))}
              </ul>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white border border-[#1A1A1A]/10 p-6 text-center">
                  <p className="font-heading text-2xl text-[#1A1A1A] mb-1">{t("securite.stat_1_value")}</p>
                  <p className="text-[#1A1A1A]/70 text-xs">{t("securite.stat_1_label")}</p>
                </div>
                <div className="bg-white border border-[#1A1A1A]/10 p-6 text-center">
                  <p className="font-heading text-2xl text-[#1A1A1A] mb-1">{t("securite.stat_2_value")}</p>
                  <p className="text-[#1A1A1A]/70 text-xs">{t("securite.stat_2_label")}</p>
                </div>
                <div className="bg-white border border-[#1A1A1A]/10 p-6 text-center">
                  <p className="font-heading text-2xl text-[#1A1A1A] mb-1">{t("securite.stat_3_value")}</p>
                  <p className="text-[#1A1A1A]/70 text-xs">{t("securite.stat_3_label")}</p>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="bg-white border border-[#1A1A1A]/10 p-8 lg:p-12">
                <h2 className="font-heading text-2xl text-[#1A1A1A] mb-2">{t("securite.quote_title")}</h2>
                <p className="text-[#1A1A1A]/70 text-sm mb-8">{t("securite.quote_desc")}</p>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="securite-form">
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("securite.name_label")} *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required className="input-luxury w-full bg-transparent" placeholder={t("securite.name_placeholder")} data-testid="input-name" />
                  </div>
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("securite.email_label")} *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className="input-luxury w-full bg-transparent" placeholder={t("securite.email_placeholder")} data-testid="input-email" />
                  </div>
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("securite.phone_label")}</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="input-luxury w-full bg-transparent" placeholder={t("securite.phone_placeholder")} data-testid="input-phone" />
                  </div>
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("securite.contact_pref")}</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="preferred_contact" value="email" checked={formData.preferred_contact === "email"} onChange={handleChange} className="accent-[#1A1A1A]" />
                        <Envelope size={18} className="text-[#1A1A1A]/60" />
                        <span className="text-[#1A1A1A]/80 text-sm">{t("common.email")}</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="preferred_contact" value="whatsapp" checked={formData.preferred_contact === "whatsapp"} onChange={handleChange} className="accent-[#1A1A1A]" />
                        <WhatsappLogo size={18} className="text-[#1A1A1A]/60" />
                        <span className="text-[#1A1A1A]/80 text-sm">{t("common.whatsapp")}</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("securite.message_label")} *</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} required rows={5} className="input-luxury w-full bg-transparent resize-none" placeholder={t("securite.message_placeholder")} data-testid="input-message" />
                  </div>
                  <button type="submit" disabled={loading} className="btn-dark w-full flex items-center justify-center gap-3 disabled:opacity-50" data-testid="submit-contact">
                    {loading ? t("securite.submitting") : t("securite.submit")}
                    <ArrowRight size={16} weight="bold" />
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SecuritePage;
